import { ArrowRightIcon, Button, Table, TableColumns } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { useColumnsSort } from '../../../../hooks/useColumnsSort';
import { useHiddenColumns } from '../../../../hooks/useHiddenColumns';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { useUserConfigContext } from '../../../../hooks/useUserConfigContext';
import { ColorType } from '../../../../types/global';
import { hexToColorOption } from '../../../../utils/colorUtils';
import { useDeputateLessons } from '../../hooks/useDeputateLessons';

type LessonClassesTableType = {
  uuid: string;
  class: string;
  planned: number | null;
  caretakers: string;
  color: ColorType;
};

type LessonClassesTableProps = {
  isEvaluation?: boolean;
  onNavigate: (classUuid: string) => void;
};

export const LessonClassesTable = observer(({ isEvaluation, onNavigate }: LessonClassesTableProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const schoolYear = useUserConfigContext().selectedSchoolYear;

  const { columnVisibility, saveColumnVisibility } = useHiddenColumns('lesson-classes-table', {
    color: !isEvaluation,
    caretakers: !isEvaluation,
    status: !isEvaluation,
    sst: !isEvaluation,
  });
  const { sorting, saveSorting } = useColumnsSort('lesson-classes-table');

  const { classesData, teacherData } = useLoadBasicData({ pause: false });

  const { lessonsData } = useDeputateLessons({
    organizationUuid: pimAuthClaims.getOrganizationUuid(),
    schoolYearUuid: schoolYear?.uuid ?? '',
  });

  const data: LessonClassesTableType[] =
    classesData?.classes
      .filter((c) => c.schoolYear.uuid === schoolYear?.uuid)
      .sort((a, b) => {
        return isEvaluation ? (a.grade ?? 0) - (b.grade ?? 0) || a.name.localeCompare(b.name) : 0;
      })
      .map((c) => {
        const { html, label } = hexToColorOption(c.timetableConfig?.color ?? '');
        return {
          class: c.name,
          uuid: c.uuid,
          caretakers:
            c.tutorsConnection.edges
              .map((tutor) => {
                const tutorEdge = teacherData?.people.find((t) => t.uuid === tutor.node.uuid);
                return tutorEdge?.fullName ?? '';
              })
              .toString() ?? '',
          planned:
            lessonsData
              .filter((ld) => ld.classes.some((lc) => lc.uuid === c.uuid))
              .map((ld) => ld.teachingLoadInfo?.deputatByClass?.get(c.uuid) ?? 0)
              .reduce((prev, cur) => prev + cur, 0) ?? 0,
          color: { color: html, colorLabel: label },
        };
      }) ?? [];

  const columns: TableColumns<LessonClassesTableType>[] = [
    {
      header: t('classes.title', { count: 1 }),
      id: 'class',
      accessorKey: 'class',
      meta: {
        filterName: t('classes.title', { count: 1 }),
      },
      size: 300,
    },
    {
      header: t('deputate.planned'),
      id: 'planned',
      accessorKey: 'planned',
      meta: {
        filterName: t('deputate.planned'),
      },
      accessorFn: (row) => {
        return row.planned?.toFixed(2);
      },
      alignment: 'right',
      size: 50,
    },
    {
      header: t('deputate.caretaker'),
      id: 'caretakers',
      accessorKey: 'caretakers',
      meta: {
        filterName: t('deputate.caretaker'),
      },
      size: 200,
    },
    {
      header: t('common.color'),
      accessorKey: 'color',
      id: 'color',
      type: 'color',
    },
  ];

  return (
    <Table<LessonClassesTableType>
      showBorderRadius
      showShadow
      showSort
      isOnWhite={false}
      showVisibility
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={saveColumnVisibility}
      sorting={sorting}
      onSortingChange={saveSorting}
      columns={columns}
      data={data}
      printerSettings={{
        headline: pimAuthClaims.getProfile()?.organization.name,
        subline: `${t('classes.title', { count: 2 })} (${t('lesson.title', { count: 2 })}) - ${t(
          'common.schoolYear',
        )} ${schoolYear?.shortName}`,
        filename: `${t('classes.title', { count: 2 })}_${t('lesson.title', { count: 2 })}_${schoolYear?.shortName}`,
      }}
      showActionBar
      actionBarSettings={{
        showExpertFilter: !isEvaluation,
        showPrintButton: true,
      }}
      showRowHover
      onRowClick={(_, row) => onNavigate(row.original.uuid)}
      lastCol={(row) => {
        return (
          <Button
            hierarchy='ghost'
            onClick={() => onNavigate(row.original.uuid)}
            icon={<ArrowRightIcon className='small' />}
          />
        );
      }}
    />
  );
});
